/**
 * BUG-497 — outside-click detection vs. portalled child popovers.
 *
 * A popover that closes itself when a `pointerdown` lands outside its own
 * container (the common `containerRef.current.contains(target)` pattern) is
 * wrong for any child control that renders through `createPortal`. The portal
 * mounts under `<body>`, so it is *not* a DOM descendant of the container —
 * interacting with it therefore reads as an "outside" click and tears the
 * parent down mid-interaction, discarding whatever the user just picked.
 *
 * This is a class of defect, not a single case: it applies to every portalled
 * control (calendars, comboboxes, nested dialogs), so the rule lives here once
 * rather than being re-derived at each call site.
 *
 * The contract: portalled content that logically belongs to a trigger tags its
 * root nodes with `data-portal-popover="<name>"`. Any ancestor running an
 * outside-click test calls {@link isInsidePortalPopover} on the event target
 * first and skips closing when it returns true.
 */

/** Attribute marking a portal root as content owned by an in-tree trigger. */
export const PORTAL_POPOVER_ATTR = "data-portal-popover";

/**
 * True when `target` lies inside (or is) a portalled popover marked with
 * {@link PORTAL_POPOVER_ATTR}.
 *
 * Accepts the raw `EventTarget` from a DOM listener so call sites do not have
 * to cast. Non-element targets (text nodes, `document`, `window`, `null`)
 * resolve to their nearest element ancestor where possible, and return `false`
 * otherwise.
 */
export function isInsidePortalPopover(target: EventTarget | null): boolean {
  const node = target as Node | null;
  if (!node) return false;

  // Text nodes have no `closest`; walk up to the nearest element.
  const element =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : (node.parentElement ?? null);
  if (!element) return false;

  return element.closest(`[${PORTAL_POPOVER_ATTR}]`) !== null;
}
